import axios, { InternalAxiosRequestConfig } from 'axios'
import { ApiResponse, PaginatedResponse } from '../types/entities'
import { apiConfig } from '../config/environment'

// Global token getter function - set by the authentication context (legacy support)
let getAccessToken: (() => Promise<string | null>) | null = null

// Token refresh function - set by AuthContext to handle automatic token refresh on 401
let attemptTokenRefresh: (() => Promise<boolean>) | null = null

export function setTokenProvider(tokenProvider: () => Promise<string | null>) {
  getAccessToken = tokenProvider
}

export function setRefreshHandler(refreshHandler: () => Promise<boolean>) {
  attemptTokenRefresh = refreshHandler
}

// CSRF token management (stored in sessionStorage to persist across page refreshes)
const CSRF_TOKEN_KEY = 'csrf_token'
let storageBlocked = false // Track if storage access is blocked to reduce console noise
let inMemoryCsrfToken: string | null = null // Fallback for when storage is blocked

function getStoredCsrfToken(): string | null {
  if (storageBlocked) {
    return inMemoryCsrfToken
  }

  try {
    return sessionStorage.getItem(CSRF_TOKEN_KEY)
  } catch (e) {
    if (!storageBlocked) {
      console.warn('Browser tracking prevention detected: sessionStorage access blocked. Falling back to in-memory storage.')
      storageBlocked = true
    }
    return inMemoryCsrfToken
  }
}

function storeCSRFToken(token: string | null) {
  inMemoryCsrfToken = token // Always store in memory as fallback

  if (storageBlocked) {
    return
  }

  try {
    if (token) {
      sessionStorage.setItem(CSRF_TOKEN_KEY, token)
    } else {
      sessionStorage.removeItem(CSRF_TOKEN_KEY)
    }
  } catch (e) {
    if (!storageBlocked) {
      console.warn('Browser tracking prevention detected: sessionStorage access blocked. Falling back to in-memory storage.')
      storageBlocked = true
    }
  }
}

export function setCsrfToken(token: string | null) {
  storeCSRFToken(token)
}

export function getCsrfToken(): string | null {
  return getStoredCsrfToken()
}

// Trial expiration callback (set by TrialExpirationProvider)
let onTrialExpired: ((message: string, trialEndedAt?: string) => void) | null = null

export function setTrialExpirationHandler(handler: (message: string, trialEndedAt?: string) => void) {
  onTrialExpired = handler
}

class ApiClient {
  private client = axios.create({
    baseURL: apiConfig.baseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Enable sending cookies with requests
  })

  constructor() {
    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor - add auth token (legacy) and CSRF token
    this.client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      // Add CSRF token to state-changing requests (POST, PUT, DELETE, PATCH)
      const isStateChangingRequest = ['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '')
      const token = getStoredCsrfToken()
      if (isStateChangingRequest && token) {
        config.headers.set('X-CSRF-Token', token)
      }

      // Legacy support: Add bearer token if using old token-based auth
      if (getAccessToken) {
        try {
          const token = await getAccessToken()
          if (token) {
            config.headers.set('Authorization', `Bearer ${token}`)
          }
        } catch (error) {
          console.error('Failed to acquire token:', error)
        }
      }
      return config
    })

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error: any) => {
        // Handle 402 Payment Required (Trial Expired)
        if (error.response?.status === 402) {
          const errorData = error.response?.data
          const message = errorData?.message || 'Your trial has expired. Please add a payment method to reactivate your account.'
          const trialEndedAt = errorData?.trialEndedAt

          // Trigger trial expiration handler if set
          if (onTrialExpired) {
            onTrialExpired(message, trialEndedAt)
          }

          // Don't continue with other error handlers
          return Promise.reject(error)
        }

        // Handle 403 CSRF failures. The CSRF cookie is browser-wide but the token
        // lives in per-tab sessionStorage, so a tab that didn't itself log in
        // (e.g. opened from an email "view ticket" link) has the cookie but no
        // token, and every state-changing request 403s. Refresh re-mints a
        // matching token+cookie pair (and stores the token), so retry once.
        const isCsrfFailure = error.response?.status === 403 &&
          typeof error.response?.data?.message === 'string' &&
          error.response.data.message.includes('CSRF')
        if (isCsrfFailure && error.config && !(error.config as any)._csrfRetried && attemptTokenRefresh) {
          (error.config as any)._csrfRetried = true
          try {
            const refreshSuccess = await attemptTokenRefresh()
            if (refreshSuccess) {
              return this.client.request(error.config)
            }
          } catch (refreshError) {
            console.error('[ApiClient] CSRF recovery refresh failed:', refreshError)
          }
        }

        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
          // Don't intercept 401 errors from login/register/refresh endpoints - let them through for proper error handling
          const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
                                 error.config?.url?.includes('/auth/register') ||
                                 error.config?.url?.includes('/auth/login/mfa') ||
                                 error.config?.url?.includes('/auth/refresh')

          if (!isAuthEndpoint && attemptTokenRefresh) {
            // Try to refresh the token
            console.log('[ApiClient] Attempting token refresh on 401 error')
            try {
              const refreshSuccess = await attemptTokenRefresh()
              if (refreshSuccess) {
                // Token refreshed successfully, retry the original request
                console.log('[ApiClient] Token refreshed, retrying request')
                return this.client.request(error.config)
              }
            } catch (refreshError) {
              console.error('[ApiClient] Token refresh failed:', refreshError)
            }
          }

          if (!isAuthEndpoint) {
            console.error('Unauthorized access - session expired, redirecting to login')

            // Only redirect if not already on login or public pages
            const publicPaths = ['/login', '/register', '/accept-invitation', '/verify-email', '/reset-password']
            const currentPath = window.location.pathname
            const isPublicPage = publicPaths.some(path => currentPath.includes(path))

            if (!isPublicPage) {
              // Redirect to login with return URL
              window.location.href = `/login?returnUrl=${encodeURIComponent(currentPath)}`
            }
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // Generic HTTP methods
  async get<T>(url: string): Promise<T> {
    try {
      const response = await this.client.get<ApiResponse<T>>(url)
      if (response.data.success && response.data.data !== undefined) {
        return response.data.data
      }
      throw new Error(response.data.message || 'Request failed')
    } catch (error: any) {
      // Extract error message from backend ApiResponse
      const message = error.response?.data?.message || error.message || 'Request failed'
      throw new Error(message)
    }
  }

  async post<T, D = any>(url: string, data?: D): Promise<T> {
    try {
      const response = await this.client.post<ApiResponse<T>>(url, data)
      if (response.data.success && response.data.data !== undefined) {
        return response.data.data
      }
      throw new Error(response.data.message || 'Request failed')
    } catch (error: any) {
      // Extract error message from backend ApiResponse or validation errors
      let message = error.response?.data?.message || error.message || 'Request failed'

      // Handle ASP.NET Core model validation errors
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors
        const errorMessages = Object.keys(errors)
          .map(key => errors[key].join(', '))
          .join('; ')
        message = errorMessages || message
      } else if (error.response?.data?.title) {
        // Handle validation error title
        message = error.response.data.title
      }

      throw new Error(message)
    }
  }

  async put<T, D = any>(url: string, data?: D): Promise<T> {
    try {
      const response = await this.client.put<ApiResponse<T>>(url, data)
      if (response.data.success && response.data.data !== undefined) {
        return response.data.data
      }
      throw new Error(response.data.message || 'Request failed')
    } catch (error: any) {
      // Extract error message from backend ApiResponse or validation errors
      let message = error.response?.data?.message || error.message || 'Request failed'

      // Handle ASP.NET Core model validation errors
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors
        const errorMessages = Object.keys(errors)
          .map(key => errors[key].join(', '))
          .join('; ')
        message = errorMessages || message
      } else if (error.response?.data?.title) {
        // Handle validation error title
        message = error.response.data.title
      }

      throw new Error(message)
    }
  }

  async delete<T>(url: string): Promise<T> {
    try {
      const response = await this.client.delete<ApiResponse<T>>(url)
      if (response.data.success && response.data.data !== undefined) {
        return response.data.data
      }
      throw new Error(response.data.message || 'Request failed')
    } catch (error: any) {
      // Extract error message from backend ApiResponse or validation errors
      let message = error.response?.data?.message || error.message || 'Request failed'

      // Handle ASP.NET Core model validation errors
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors
        const errorMessages = Object.keys(errors)
          .map(key => errors[key].join(', '))
          .join('; ')
        message = errorMessages || message
      } else if (error.response?.data?.title) {
        // Handle validation error title
        message = error.response.data.title
      }

      throw new Error(message)
    }
  }

  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    try {
      const response = await this.client.post<ApiResponse<T>>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.data.success && response.data.data !== undefined) {
        return response.data.data
      }
      throw new Error(response.data.message || 'Request failed')
    } catch (error: any) {
      // Extract error message from backend ApiResponse
      const message = error.response?.data?.message || error.message || 'Request failed'
      throw new Error(message)
    }
  }

  // Convenience methods for common patterns
  async getWithResponse<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url)
    return response.data
  }

  async getPaginated<T>(url: string, page = 1, pageSize = 20): Promise<PaginatedResponse<T>> {
    const params = new URLSearchParams({
      pageNumber: page.toString(),
      pageSize: pageSize.toString(),
    })
    return this.get<PaginatedResponse<T>>(`${url}?${params}`)
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Export individual service modules
export * from './TicketService'
export * from './UserService'
export * from './CategoryService'
export * from './OrganizationService'
export * from './AnalyticsService'
export * from './EscalationService'
